import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import apiFactory from "../api";

import * as mediasoupClient from "mediasoup-client";
import { useInfoUser } from "../store/UserStore";
import { chatEvents$ } from "../api/webSocketRx";
import { debounce } from "lodash";
import { useAuthLayoutContext } from "./AuthLayoutContext";

const CallingContext = createContext(null);

export const useCallingContext = () => {
  return useContext(CallingContext);
};

/*
 * screen: false
 * video: false
 * audio: false
 * isLostScreen: false
 * isVideoLost: false
 * isAudioLost: false
 *
 * videoStream: stream
 * screenStream: stream
 * */

export const CallingProvider = ({ children }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useInfoUser();
  const { getMember } = useAuthLayoutContext();

  const [room, setRoom] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [localStream, setLocalStream] = useState({
    screen: false,
    video: false,
    audio: false,
    isLostScreen: false,
    isVideoLost: false,
    isAudioLost: false,
  });

  const [device, setDevice] = useState(null);

  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedAudioId, setSelectedAudioId] = useState(null);
  const [selectedVideoId, setSelectedVideoId] = useState(null);

  const [isModalAddMember, setIsModalAddMember] = useState(false);

  const [focusId, setFocusId] = useState(null);

  /*
  localStream to render
  localStreamRef is stream to close camera and mic after leaving
  */
  const localStreamRef = useRef(null);

  const isMediaLostRef = useRef({
    screen: true,
    video: false,
    audio: false,
  });

  const totalDeviceRef = useRef({
    video: 0,
    audio: 0,
  });

  const videoProducerRef = useRef(null);
  const audioProducerRef = useRef(null);
  const screenProducerRef = useRef(null);

  const consumersRef = useRef(new Map());

  const producerTransportRef = useRef(null);
  const consumerTransportRef = useRef(null);
  const initWebRTCStartedRef = useRef(false);

  const timeoutRef = useRef();

  /*
   * remoteStream structure
   * 1. targetId
   * 2. video boolean
   * 3. audio boolean
   * 4. new MediaStream())
   * */
  const [remoteStreams, setRemoteStreams] = useState([]);

  const onFocusMember = (memberId) => {
    setFocusId(memberId);
  };

  const cancelFocusMember = () => {
    setFocusId(null);
  };

  const fetchRoom = async () => {
    const roomId = searchParams?.get("room_id");

    if (roomId) {
      const result = await apiFactory.callingApi.getRoom({
        roomId,
        isSender: searchParams?.get("isSender"),
      });

      if (result?.data === null) {
        clearRoom();
        setRoom(null);
        return false;
      }

      setRoom({
        ...result?.data,
        members: result?.data?.members?.map((member) => ({
          ...member,
          status: member?.mood,
        })),
      });

      if (searchParams?.get("isSender")) {
        const activeMembers = result?.data?.members?.filter(
          (member) => !["REJECTED", "STOPPED"].includes(member.mood),
        );

        if (activeMembers?.length < 2) {
          closeRoom();
          return false;
        } else {
          setTimeout(() => {
            setRoom((prev) => ({
              ...prev,
              members: prev?.members?.filter(
                (member) => member.mood !== "REJECTED",
              ),
            }));
          }, 10_000);
        }
      }

      return true;
    }
  };

  const consumeStream = async ({
    consumerId,
    producerId,
    rtpParameters,
    kind,
  }) => {
    if (!consumerTransportRef.current) {
      throw new Error("Transport not ready");
    }
    try {
      const consumer = await consumerTransportRef.current.consume({
        id: consumerId,
        producerId,
        kind: kind === "audio" ? "audio" : "video",
        rtpParameters,
      });
      return consumer;
    } catch (err) {
      console.error("Consume error:", err);
      throw err;
    }
  };

  const initWebRTC = async () => {
    if (!room || !user) return;

    if (!localStream?.audio && !localStream?.video) return;

    if (initWebRTCStartedRef.current) return;
    initWebRTCStartedRef.current = true;

    try {
      if (room?.creatorId === user?.userId)
        await apiFactory.callingApi.ring({ roomId: room?.roomId });

      const rtpResponse = await apiFactory.cameraApi.getRtpCapabilities({
        roomId: room?.roomId,
      });

      if (rtpResponse?.status !== 200) {
        return;
      }

      const newDevice = new mediasoupClient.Device();
      await newDevice.load({ routerRtpCapabilities: rtpResponse?.data });
      setDevice(newDevice);

      await sendStreaming(newDevice);

      const members = room?.members?.filter(
        (member) =>
          member?.memberId !== user?.userId && member?.mood === "PRODUCING",
      );

      if (!members?.length) return;

      for (const member of members) {
        await receiveStreaming(newDevice, member?.memberId, "video");
        await receiveStreaming(newDevice, member?.memberId, "audio");
      }

      if (room?.speaker) {
        await receiveScreenStreaming(
          newDevice,
          room?.speaker?.memberId,
          "screen",
        );
      }
    } catch (err) {
      console.error("❌ [WebRTC] Init failed", err);
    }
  };

  const sendStreaming = async (device) => {
    if (!device) {
      toast.error("Hệ thống WebRTC chưa sẵn sàng!");
      return;
    }

    try {
      const response = await apiFactory.cameraApi.createProducerTransport({
        userId: user?.userId,
        roomId: room?.roomId,
      });

      const videoTrack = localStream?.videoStream?.getVideoTracks()[0];
      const audioTrack = localStream?.videoStream?.getAudioTracks()[0];

      const producerTransport = device.createSendTransport({
        id: response?.data?.id,
        iceParameters: response?.data?.iceParameters,
        iceCandidates: response?.data?.iceCandidates,
        dtlsParameters: response?.data?.dtlsParameters,
      });

      producerTransportRef.current = producerTransport;

      producerTransport.on(
        "connect",
        async ({ dtlsParameters }, callback, errback) => {
          try {
            await apiFactory.cameraApi.connectTransport({
              userId: user?.userId,
              dtlsParameters,
              direction: "send",
            });
            callback();
          } catch (error) {
            errback(error);
          }
        },
      );

      producerTransport.on("produce", async (parameters, callback, errback) => {
        try {
          const { kind, rtpParameters, appData } = parameters;

          const res = await apiFactory.cameraApi.produce({
            userId: user?.userId,
            kind: appData?.source || kind,
            rtpParameters,
            roomId: room?.roomId,
          });

          await apiFactory.callingApi.produce({
            roomId: room?.roomId,
            producedMemberId: user?.userId,
            kind: appData?.source || kind,
          });
          callback({ id: res?.data?.producerId });
        } catch (error) {
          errback(error);
        }
      });

      if (videoTrack) {
        videoProducerRef.current = await producerTransport.produce({
          track: videoTrack,
          // encodings: [
          //   {
          //     scaleResolutionDownBy: 1,
          //     maxBitrate: 2_500_000,
          //     maxFramerate: 24,
          //   },
          // ],
          //
          // codecOptions: {
          //   videoGoogleStartBitrate: 1500,
          // },
        });

        videoTrack.onended = () => {
          lostCamera();
        };
      }

      if (audioTrack) {
        audioProducerRef.current = await producerTransport.produce({
          track: audioTrack,
        });

        audioTrack.onended = () => {
          lostMic();
        };
      }
    } catch (error) {
      console.error("Lỗi khi publish webcam:", error);
      toast.error("Không thể bật webcam! Vui lòng kiểm tra quyền.");
    }
  };

  const replaceStream = async (stream, kind) => {
    let oldTrack;

    if (kind === "video" || kind === "screen") {
      oldTrack = videoProducerRef?.current?.track;

      const newVideoTrack = stream?.getVideoTracks()[0];
      newVideoTrack.onended = () => {
        lostCamera();
      };
      await videoProducerRef?.current.replaceTrack({
        track: newVideoTrack,
      });
    } else {
      oldTrack = audioProducerRef?.current?.track;

      const newAudioTrack = stream?.getAudioTracks()[0];
      newAudioTrack.onended = () => {
        lostMic();
      };
      await audioProducerRef?.current.replaceTrack({
        track: newAudioTrack,
      });
    }

    oldTrack?.stop();
  };

  const receiveStreaming = async (device, targetId, kind) => {
    if (!targetId || targetId === user?.userId) {
      return;
    }

    clearTimer();
    await getOrCreateConsumerTransport(device);

    const consumerInfo = await apiFactory.cameraApi.consume({
      userId: user?.userId,
      kind: kind,
      rtpCapabilities: device.rtpCapabilities,
      targetId: targetId,
      roomId: room?.roomId,
    });

    if (consumerInfo?.status !== 200) {
      return;
    }

    const consumerStream = await consumeStream({
      consumerId: consumerInfo?.data?.id,
      producerId: consumerInfo?.data?.producerId,
      kind: consumerInfo?.data?.kind,
      rtpParameters: consumerInfo?.data?.rtpParameters,
    });

    consumersRef.current.set(`${targetId}:${kind}`, consumerStream);

    setRemoteStreams((prev) => {
      const rIndex = prev.findIndex((item) => item.targetId === targetId);

      if (rIndex !== -1) {
        if (kind === "video" && prev?.[rIndex]?.video) return [...prev];
        if (kind === "audio" && prev?.[rIndex]?.audio) return [...prev];

        if (kind === "video") {
          prev[rIndex].video = true;
        }

        if (kind === "audio") {
          prev[rIndex].audio = true;
        }

        if (!prev?.[rIndex]?.videoStream)
          prev[rIndex].videoStream = new MediaStream();
        prev?.[rIndex]?.videoStream?.addTrack(consumerStream.track);
      } else {
        const stream = new MediaStream();
        stream.addTrack(consumerStream.track);

        prev?.push({
          targetId: targetId,
          video: kind === "video",
          audio: kind === "audio",
          videoStream: stream,
        });
      }

      return [...prev];
    });

    setRoom((prev) => {
      const mIndex = prev?.members?.findIndex(
        (item) => item.memberId === targetId,
      );
      prev.members[mIndex].status = "PRODUCING";
      return prev;
    });

    await apiFactory.cameraApi.resume({
      userId: user?.userId,
      targetId: targetId,
      kind,
    });

    consumerStream.resume();
  };

  const receiveScreenStreaming = async (device, targetId, kind) => {
    if (!targetId || targetId === user?.userId) {
      return;
    }

    const member = getMember(targetId);

    await getOrCreateConsumerTransport(device);

    const consumerInfo = await apiFactory.cameraApi.consume({
      userId: user?.userId,
      kind: kind,
      rtpCapabilities: device.rtpCapabilities,
      targetId: targetId,
      roomId: room?.roomId,
    });

    if (consumerInfo?.status !== 200) {
      return;
    }

    const consumerStream = await consumeStream({
      consumerId: consumerInfo?.data?.id,
      producerId: consumerInfo?.data?.producerId,
      kind: consumerInfo?.data?.kind,
      rtpParameters: consumerInfo?.data?.rtpParameters,
    });

    consumersRef.current.set(`${targetId}:${kind}`, consumerStream);

    setRemoteStreams((prev) => {
      const rIndex = prev.findIndex(
        (item) => item.targetId === targetId + ":screen",
      );

      if (rIndex !== -1) {
        prev[rIndex].screenStream = new MediaStream();
        prev?.[rIndex]?.screenStream?.addTrack(consumerStream.track);
      } else {
        const stream = new MediaStream();
        stream.addTrack(consumerStream.track);

        prev?.push({
          targetId: targetId + ":screen",
          screenStream: stream,
        });
      }

      return [...prev];
    });

    setRoom((prev) => {
      const mIndex = prev?.members?.findIndex((item) => item.isScreen === true);

      if (mIndex !== -1) {
        prev.members[mIndex] = {
          memberId: targetId,
          memberName: member?.name,
          isScreen: true,
        };
      } else {
        prev?.members?.push({
          memberId: targetId,
          memberName: member?.name,
          isScreen: true,
        });
      }

      prev.speaker = null;

      return prev;
    });

    await apiFactory.cameraApi.resume({
      userId: user?.userId,
      targetId: targetId,
      kind,
    });

    consumerStream.resume();

    setFocusId((prev) => {
      if (prev?.includes("screen")) {
        prev = targetId + ":screen";
      }

      return prev;
    });

    await abc();
  };

  const closeConsumer = async ({ targetId, roomId, type }) => {
    if (targetId === user?.userId || roomId !== room?.roomId) return;

    const kind = type === "TURN_OFF_VIDEO" ? "video" : "audio";

    const key = `${targetId}:${kind}`;
    const consumerStream = consumersRef.current.get(key);

    if (consumerStream) {
      consumerStream?.close();
      consumersRef.current.delete(key);
    }

    setRemoteStreams((prev) =>
      prev.map((item) => {
        if (item.targetId !== targetId) return item;

        const remainTracks =
          kind === "video"
            ? item.videoStream?.getAudioTracks() || []
            : item.videoStream?.getVideoTracks() || [];

        return {
          ...item,
          [kind]: false,
          videoStream: new MediaStream(remainTracks),
        };
      }),
    );
  };

  const closeScreenConsumer = async ({ targetId, roomId }) => {
    if (targetId === user?.userId || roomId !== room?.roomId) return;

    const key = `${targetId}:screen`;
    const consumerStream = consumersRef.current.get(key);

    if (consumerStream) {
      consumerStream?.close();
      consumersRef.current.delete(key);

      setFocusId((prev) => {
        if (prev === key) return null;
        return prev;
      });

      setRemoteStreams((prev) => {
        const rIndex = prev.findIndex(
          (item) => item.targetId === targetId + ":screen",
        );

        if (rIndex !== -1) prev?.splice(rIndex, 1);

        return [...prev];
      });

      setRoom((prev) => {
        const mIndex = prev?.members?.findIndex(
          (item) => item?.memberId === targetId && item.isScreen === true,
        );

        if (mIndex !== -1) prev?.members?.splice(mIndex, 1);
        return prev;
      });
    }
  };

  const getOrCreateConsumerTransport = async (device) => {
    if (consumerTransportRef.current && !consumerTransportRef.current.closed) {
      return consumerTransportRef.current;
    }

    const response = await apiFactory.cameraApi.createConsumerTransport({
      userId: user?.userId,
      roomId: room?.roomId,
    });

    const consumerTransport = device.createRecvTransport(response?.data);

    consumerTransport.on(
      "connect",
      async ({ dtlsParameters }, callback, errback) => {
        try {
          await apiFactory.cameraApi.connectTransport({
            userId: user?.userId,
            dtlsParameters,
            direction: "recv",
          });

          callback();
        } catch (error) {
          errback(error);
        }
      },
    );

    consumerTransport.on("connectionstatechange", (state) => {
      console.log("[recvTransport] state:", state);

      if (state === "failed" || state === "closed") {
        consumerTransportRef.current = null;
      }
    });

    consumerTransportRef.current = consumerTransport;

    return consumerTransport;
  };

  const stopStream = (stream) => {
    stream?.getTracks()?.forEach((track) => track?.stop());
  };

  const fetchLocalStream = async (videoDeviceId, audioDeviceId) => {
    if (!videoDeviceId && !audioDeviceId) {
      return;
    }

    stopStream(localStreamRef?.current);

    // const constraints = {
    //   video: videoDeviceId
    //     ? {
    //         deviceId: {
    //           exact: videoDeviceId,
    //         },
    //         width: {
    //           ideal: 1280,
    //           max: 1280,
    //         },
    //         height: {
    //           ideal: 720,
    //           max: 720,
    //         },
    //         frameRate: {
    //           ideal: 24,
    //           max: 24,
    //         },
    //         aspectRatio: {
    //           ideal: 16 / 9,
    //         },
    //       }
    //     : false,
    //
    //   audio: audioDeviceId
    //     ? {
    //         deviceId: {
    //           exact: audioDeviceId,
    //         },
    //         echoCancellation: true,
    //         noiseSuppression: true,
    //         autoGainControl: true,
    //       }
    //     : false,
    // };
    const constraints = {
      video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : false,
      audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : false,
    };

    const videoStream = await navigator.mediaDevices.getUserMedia(constraints);

    localStreamRef.current = videoStream;
    setLocalStream({
      screen: false,
      audio: !!audioDeviceId,
      video: !!videoDeviceId,
      isAudioLost: !audioDeviceId,
      isVideoLost: !videoDeviceId,
      isLostScreen: false,
      videoStream,
    });
    isMediaLostRef.current.video = !videoDeviceId;
    isMediaLostRef.current.audio = !audioDeviceId;
  };

  const fetchLocalDevices = async () => {
    try {
      if (!user) return;

      /* permission to get devices */


      const mode = searchParams?.get("mode");

      const constraints = {};
      if (mode === "audio") {
        constraints.audio = true;
      } else if (mode === "video") {
        constraints.video = true;
      } else {
        constraints.audio = true;
        constraints.video = true;
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const devices = await navigator.mediaDevices.enumerateDevices();
      /* stop permission stream */
      stream?.getTracks()?.forEach((t) => t.stop());

      const videoDevices = devices
        .filter((device) => device?.kind === "videoinput")
        .map((device) => ({
          deviceId: device.deviceId,
          kind: device.kind,
          label: device.label,
          groupId: device.groupId,
          value: device.deviceId,
        }));
      const audioDevices = devices
        .filter(
          (device) =>
            device?.kind === "audioinput" &&
            device.deviceId !== "default" &&
            device.deviceId !== "communications",
        )
        .map((device) => ({
          deviceId: device.deviceId,
          kind: device.kind,
          label: device.label,
          groupId: device.groupId,
          value: device.deviceId,
        }));

      if (mode === "audio" && !audioDevices?.length) {
        navigateAudioError();
        return;
      }

      totalDeviceRef.current.video = videoDevices?.length;
      totalDeviceRef.current.audio = audioDevices?.length;

      setVideoDevices(videoDevices);
      setAudioDevices(audioDevices);

      if (mode === "audio") {
        if (audioDevices.length) {
          setSelectedAudioId(audioDevices[0].deviceId);
          await fetchLocalStream(null, audioDevices[0].deviceId);
        }
      } else if (mode === "video") {
        if (videoDevices.length) {
          setSelectedVideoId(videoDevices[0].deviceId);
        }
        if (audioDevices.length) {
          setSelectedAudioId(audioDevices[0].deviceId);
        }

        await fetchLocalStream(
          videoDevices?.[0]?.deviceId ?? null,
          audioDevices?.[0]?.deviceId ?? null,
        );
      } else {
        if (audioDevices.length) {
          setSelectedAudioId(audioDevices[0].deviceId);
        }

        if (videoDevices.length) {
          setSelectedVideoId(videoDevices[0].deviceId);
        }

        await fetchLocalStream(
          videoDevices?.[0]?.deviceId,
          audioDevices?.[0]?.deviceId,
        );
      }
    } catch (e) {
      navigateAudioError();
    }
  };

  const turnCamera = async () => {
    if (localStream?.video) {
      await hideCamera();
    } else {
      await onCamera();
    }
  };

  const turnMic = async () => {
    if (localStream?.audio) {
      await hideMic();
    } else {
      await onMic();
    }
  };

  const onSharingScreen = async () => {
    const displayStream = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: 60 },
      audio: false,
    });

    const screenTrack = displayStream.getVideoTracks()[0];

    screenTrack.onended = () => {
      closeSharingScreen();
    };

    screenProducerRef.current = await producerTransportRef.current.produce({
      track: screenTrack,
      appData: {
        source: "screen",
      },
    });

    setLocalStream((prev) => {
      // localStreamRef.current = newStream;
      isMediaLostRef.current.screen = false;

      return {
        ...prev,
        screen: true,
        isLostScreen: false,
        screenStream: displayStream,
      };
    });

    setRoom((prev) => {
      const mIndex = prev?.members?.findIndex((item) => item.isScreen === true);

      if (mIndex !== -1) {
        prev.members[mIndex] = {
          memberId: user?.userId,
          memberName: user?.name,
          isScreen: true,
        };
      } else {
        prev?.members?.push({
          memberId: user?.userId,
          memberName: user?.name,
          isScreen: true,
        });
      }

      // prev.speaker = null;

      return prev;
    });

    setFocusId((prev) => {
      if (prev != null) prev = user?.userId + ":screen";
      return prev;
    });
  };

  const closeSharingScreen = async () => {
    // stop producing screen
    screenProducerRef.current?.close();

    localStream?.screenStream?.getTracks()?.forEach((t) => t.stop());

    if (focusId === user?.userId + ":screen") setFocusId(null);

    setLocalStream((prev) => ({
      ...(prev || {}),
      screen: false,
      isLostScreen: false,
      screenStream: null,
    }));

    setRoom((prev) => {
      const screenIndex = prev?.members?.findIndex(
        (member) => member?.memberId === user?.userId && member?.isScreen,
      );

      if (screenIndex > -1) {
        prev?.members?.splice(screenIndex, 1);
      }

      return { ...prev };
    });

    await apiFactory.cameraApi.closeProducer({
      userId: user?.userId,
      roomId: room?.roomId,
      kind: "screen",
    });
  };

  const abc = async () => {
    // stop producing screen
    screenProducerRef.current?.close();

    localStream?.screenStream?.getTracks()?.forEach((t) => t.stop());

    setLocalStream((prev) => ({
      ...(prev || {}),
      screen: false,
      isLostScreen: false,
      screenStream: null,
    }));

    await apiFactory.cameraApi.closeProducer({
      userId: user?.userId,
      roomId: room?.roomId,
      kind: "screen",
    });
  };

  const onCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: selectedVideoId
          ? { deviceId: { exact: selectedVideoId } }
          : true,
        audio: false,
      });

      const videoTrack = stream.getVideoTracks()[0];

      videoTrack.onended = () => {
        lostCamera();
      };

      videoProducerRef.current = await producerTransportRef.current.produce({
        track: videoTrack,
        appData: {
          source: "video",
        },
      });

      setLocalStream((prev) => {
        const newStream = new MediaStream([
          videoTrack,
          ...(prev?.stream?.getAudioTracks() || []),
        ]);

        localStreamRef.current = newStream;
        isMediaLostRef.current.video = false;

        return {
          ...prev,
          video: true,
          isVideoLost: false,
          videoStream: newStream,
        };
      });
    } catch (e) {
      console.log(e);
    }
  };

  const onCamera2 = async (id) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: id } },
        audio: false,
      });

      const videoTrack = stream.getVideoTracks()[0];

      videoTrack.onended = () => {
        lostCamera();
      };

      videoProducerRef.current = await producerTransportRef.current.produce({
        track: videoTrack,
        appData: {
          source: "video",
        },
      });

      setLocalStream((prev) => {
        const newStream = new MediaStream([
          videoTrack,
          ...(prev?.stream?.getAudioTracks() || []),
        ]);

        localStreamRef.current = newStream;
        isMediaLostRef.current.video = false;

        return {
          ...prev,
          video: true,
          isVideoLost: false,
          videoStream: newStream,
        };
      });
    } catch (e) {
      console.log(e);
    }
  };

  const onMic = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: selectedAudioId ? { deviceId: { exact: selectedAudioId } } : true,
      video: false,
    });

    const audioTrack = stream.getAudioTracks()[0];

    audioTrack.onended = () => {
      lostMic();
    };

    audioProducerRef.current = await producerTransportRef.current.produce({
      track: audioTrack,
      appData: {
        source: "audio",
      },
    });

    setLocalStream((prev) => {
      const newStream = new MediaStream([
        audioTrack,
        ...(prev?.videoStream?.getVideoTracks() || []),
      ]);

      localStreamRef.current = newStream;
      isMediaLostRef.current.audio = false;

      return {
        ...prev,
        audio: true,
        isAudioLost: false,
        videoStream: newStream,
      };
    });
  };

  const onMic2 = async (id) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: { exact: id } },
      video: false,
    });

    const audioTrack = stream.getAudioTracks()[0];

    audioTrack.onended = () => {
      lostMic();
    };

    audioProducerRef.current = await producerTransportRef.current.produce({
      track: audioTrack,
      appData: {
        source: "audio",
      },
    });

    setLocalStream((prev) => {
      const newStream = new MediaStream([
        audioTrack,
        ...(prev?.videoStream?.getVideoTracks() || []),
      ]);

      localStreamRef.current = newStream;
      isMediaLostRef.current.audio = false;

      return {
        ...prev,
        audio: true,
        isAudioLost: false,
        videoStream: newStream,
      };
    });
  };

  const closeVideoProducer = async () => {
    videoProducerRef.current?.close();
    videoProducerRef.current = null;

    localStreamRef.current?.getVideoTracks()?.forEach((track) => track.stop());

    await apiFactory.cameraApi.closeProducer({
      userId: user?.userId,
      roomId: room?.roomId,
      kind: "video",
    });

    const newStream = new MediaStream(
      localStreamRef.current?.getAudioTracks() || [],
    );

    localStreamRef.current = newStream;
  };

  const closeAudioProducer = async () => {
    audioProducerRef.current?.close();
    audioProducerRef.current = null;

    localStreamRef.current?.getAudioTracks()?.forEach((track) => track.stop());

    await apiFactory.cameraApi.closeProducer({
      userId: user?.userId,
      roomId: room?.roomId,
      kind: "audio",
    });

    const newStream = new MediaStream(
      localStreamRef.current?.getVideoTracks() || [],
    );

    localStreamRef.current = newStream;
  };

  const lostCamera = async () => {
    isMediaLostRef.current.video = true;
    if (totalDeviceRef.current.video === 1) {
      await closeVideoProducer();
    }

    setLocalStream((prev) => ({
      ...prev,
      isVideoLost: true,
      videoStream: localStreamRef.current,
    }));
  };

  const hideCamera = async () => {
    await closeVideoProducer();

    setLocalStream((prev) => ({
      ...prev,
      video: false,
      videoStream: localStreamRef.current,
    }));
  };

  const lostMic = async () => {
    isMediaLostRef.current.audio = true;
    if (totalDeviceRef.current.audio === 1) {
      await closeAudioProducer();
    }
    setLocalStream((prev) => ({
      ...prev,
      isAudioLost: true,
      videoStream: localStreamRef.current,
    }));
  };

  const hideMic = async () => {
    await closeAudioProducer();
    setLocalStream((prev) => ({
      ...prev,
      audio: false,
      videoStream: localStreamRef.current,
    }));
  };

  const navigateAudioError = () => {
    apiFactory.cameraApi.leave({
      userId: user?.userId,
      roomId: searchParams?.get("room_id"),
    });

    navigate("/feed-back?audio_error=true");
  };

  const leaveCall = () => {
    apiFactory.cameraApi.leave({
      userId: user?.userId,
      roomId: room?.roomId,
    });

    // dont need to wait leaveCall
    // 1. close producer
    videoProducerRef.current?.close();
    audioProducerRef.current?.close();
    videoProducerRef.current = null;
    audioProducerRef.current = null;
    // 2. close consumer
    consumersRef.current?.forEach((consumer) => consumer.close());
    consumersRef.current?.clear();
    // 3. close transport
    producerTransportRef.current?.close();
    consumerTransportRef.current?.close();
    producerTransportRef.current = null;
    consumerTransportRef.current = null;
    // 4. stop camera/mic
    stopStream(localStreamRef?.current);
    localStreamRef.current = null;
    setLocalStream(null);

    setRemoteStreams([]);
    navigate("/feed-back");
  };

  const closeRoom = () => {
    // 1. close producer
    videoProducerRef.current?.close();
    audioProducerRef.current?.close();
    videoProducerRef.current = null;
    audioProducerRef.current = null;
    // 2. close consumer
    consumersRef.current?.forEach((consumer) => consumer.close());
    consumersRef.current?.clear();
    // 3. close transport
    producerTransportRef.current?.close();
    consumerTransportRef.current?.close();
    producerTransportRef.current = null;
    consumerTransportRef.current = null;
    // 4. stop camera/mic
    stopStream(localStreamRef?.current);
    localStreamRef.current = null;
    setLocalStream(null);

    setRemoteStreams([]);
    navigate("/feed-back");
  };

  const clearRoom = () => {
    // 1. close producer
    videoProducerRef.current?.close();
    audioProducerRef.current?.close();
    videoProducerRef.current = null;
    audioProducerRef.current = null;
    // 2. close transport
    producerTransportRef.current?.close();
    producerTransportRef.current = null;
    // 4. stop camera/mic
    stopStream(localStreamRef?.current);
    localStreamRef.current = null;
    setLocalStream(null);
  };

  const startTimer = () => {
    clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      clearTimer();
      closeRoom();
    }, 87_000);
  };

  const clearTimer = () => {
    if (timeoutRef?.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleDeviceChange = useMemo(
    () =>
      debounce(async () => {
        const newDevices = await navigator.mediaDevices.enumerateDevices();

        const newVideos = newDevices
          .filter((device) => device?.kind === "videoinput")
          .map((device) => ({
            deviceId: device.deviceId,
            kind: device.kind,
            label: device.label,
            groupId: device.groupId,
            value: device.deviceId,
          }));

        const newAudios = newDevices
          .filter(
            (device) =>
              device.kind === "audioinput" &&
              device.deviceId !== "default" &&
              device.deviceId !== "communications",
          )
          .map((device) => ({
            deviceId: device.deviceId,
            kind: device.kind,
            label: device.label,
            groupId: device.groupId,
            value: device.deviceId,
          }));

        if (newVideos?.length < videoDevices?.length) {
          if (!newVideos?.length) {
            setSelectedVideoId(null);
          } else {
            setSelectedVideoId(newVideos?.[0].deviceId);
            const videoStream = await navigator.mediaDevices.getUserMedia({
              video: { deviceId: { exact: newVideos?.[0].deviceId } },
            });

            setLocalStream((prev) => {
              const newStream = new MediaStream([
                videoStream.getVideoTracks()[0],
                ...(prev?.stream?.getAudioTracks() || []),
              ]);

              localStreamRef.current = newStream;
              isMediaLostRef.current.video = false;

              return {
                ...prev,
                video: true,
                isVideoLost: false,
                videoStream: newStream,
              };
            });
            await replaceStream(videoStream, "video");
          }
        }

        if (newVideos?.length > videoDevices?.length) {
          if (!selectedVideoId && localStream?.isVideoLost) {
            setSelectedVideoId(newVideos?.[0]?.deviceId);
            await onCamera2(newVideos?.[0]?.deviceId);
          }
        }

        setVideoDevices(newVideos);

        if (newAudios.length < audioDevices.length) {
          if (!newAudios.length) {
            setSelectedAudioId(null);
          } else {
            setSelectedAudioId(newAudios[0].deviceId);
            const audioStream = await navigator.mediaDevices.getUserMedia({
              audio: { deviceId: { exact: newAudios?.[0].deviceId } },
            });

            setLocalStream((prev) => {
              const newStream = new MediaStream([
                audioStream.getAudioTracks()[0],
                ...(prev?.stream?.getVideoTracks() || []),
              ]);

              localStreamRef.current = newStream;
              isMediaLostRef.current.audio = false;

              return {
                ...prev,
                audio: true,
                isAudioLost: false,
                videoStream: newStream,
              };
            });

            await replaceStream(audioStream, "audio");
          }
        }

        if (newAudios.length > audioDevices.length) {
          if (!selectedAudioId && localStream?.isAudioLost) {
            setSelectedAudioId(newAudios[0].deviceId);
            await onMic2(newAudios[0].deviceId);
          }
        }

        setAudioDevices(newAudios);

        totalDeviceRef.current.video = newVideos?.length;
        totalDeviceRef.current.audio = newAudios?.length;
      }, 500),
    [
      videoDevices,
      audioDevices,
      selectedVideoId,
      selectedAudioId,
      localStream?.isVideoLost,
      localStream?.isAudioLost,
    ],
  );

  const initCall = async () => {
    if (!user) return;

    const init = await fetchRoom();
    if (!init) return;
    await fetchLocalDevices();
  };

  const receiveAddMember = (newMembers) => {
    setRoom((prev) => {
      if (!prev) return prev;

      newMembers.forEach((member) => {
        member.status = member?.mood;
      });

      return {
        ...prev,
        members: [...(prev.members ?? []), ...newMembers],
      };
    });

    if (timeoutRef.current !== null) {
      startTimer();
    }
  };

  useEffect(() => {
    if (!user?.userId || !searchParams?.get("isSender")) return;

    startTimer();

    return () => clearTimer();
  }, [user?.userId]);

  useEffect(() => {
    if (!user) return;

    const sub = chatEvents$.subscribe((data) => {
      if (!data) return;

      switch (data?.type) {
        case "REJECTED":
        case "STOPPED":
          if (data?.call?.announcer?.userId === user?.userId) {
            closeRoom();
          } else {
            /* expired timeout -> expiredMembers */
            /* reject -> rejected announcer */

            const rejectedAnnouncerId = data?.call?.announcer?.userId;
            const expiredMemberIds =
              data?.call?.members?.map((member) => member?.memberId) || [];

            setFocusId((prev) => {
              if (
                prev === rejectedAnnouncerId ||
                prev === rejectedAnnouncerId + ":screen"
              )
                return null;
              return prev;
            });

            setRoom((prev) => ({
              ...prev,
              members:
                prev?.members?.filter(
                  (member) =>
                    member?.memberId !== rejectedAnnouncerId &&
                    !expiredMemberIds?.includes(member?.memberId),
                ) || [],
            }));

            setRemoteStreams((prev) => {
              const rIndex = prev.findIndex(
                (item) => item.targetId === rejectedAnnouncerId,
              );

              if (rIndex !== -1) {
                prev?.splice(rIndex, 1);
              }

              return [...prev];
            });
          }

          break;

        case "CLOSED_ROOM":
          leaveCall();
          break;

        case "PRODUCED_VIDEO":
          receiveStreaming(device, data?.call?.producedMemberId, "video");
          break;
        case "PRODUCED_AUDIO":
          receiveStreaming(device, data?.call?.producedMemberId, "audio");
          break;
        case "PRODUCED_SCREEN":
          receiveScreenStreaming(
            device,
            data?.call?.producedMemberId,
            "screen",
          );
          break;
        case "TURN_OFF_VIDEO":
        case "TURN_OFF_AUDIO":
          closeConsumer(data?.call);
          break;
        case "TURN_OFF_SCREEN":
          closeScreenConsumer(data?.call);
          break;
        case "ADD_MEMBER":
          receiveAddMember(data?.call?.members);
          break;
        default:
          return;
      }
    });

    return () => sub.unsubscribe();
  }, [user, room?.roomId, device]);

  useEffect(() => {
    initWebRTC();
  }, [localStream?.video, localStream?.audio, user, room?.roomId]);

  useEffect(() => {
    initCall();
  }, [user]);

  useEffect(() => {
    if (!navigator.mediaDevices?.addEventListener) return;

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange,
      );
    };
  }, [handleDeviceChange]);

  const values = useMemo(
    () => ({
      isLoading,
      setIsLoading,
      leaveCall,
      room,
      localStream,
      setLocalStream,
      initWebRTC,
      remoteStreams,
      audioDevices,
      videoDevices,
      selectedAudioId,
      selectedVideoId,
      setSelectedAudioId,
      setSelectedVideoId,
      stopStream,
      replaceStream,
      turnCamera,
      turnMic,
      timeoutRef,
      localStreamRef,
      isModalAddMember,
      setIsModalAddMember,
      onSharingScreen,
      closeSharingScreen,
      onFocusMember,
      cancelFocusMember,
      focusId,
    }),
    [
      isLoading,
      room,
      localStream,
      remoteStreams,
      audioDevices,
      videoDevices,
      selectedAudioId,
      selectedVideoId,
      isModalAddMember,
      focusId,
    ],
  );
  return (
    <CallingContext.Provider value={values}>{children}</CallingContext.Provider>
  );
};
